import { useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { SubmitToSiiResponse, QuerySiiStatusResponse } from "../types";

export interface UseSubmitToSiiResult {
  submitDocuments: (documentIds: string[]) => Promise<SubmitToSiiResponse>;
  queryStatus: (documentId: string) => Promise<QuerySiiStatusResponse>;
  isSubmitting: boolean;
  isQuerying: boolean;
  error: Error | null;
}

export function useSubmitToSii(): UseSubmitToSiiResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isQuerying, setIsQuerying] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submitDocuments = async (
    documentIds: string[]
  ): Promise<SubmitToSiiResponse> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiFetch.post<SubmitToSiiResponse>(
        "/api/tax-documents/submit",
        { documentIds }
      );
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to submit to SII");
      setError(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const queryStatus = async (documentId: string): Promise<QuerySiiStatusResponse> => {
    setIsQuerying(true);
    setError(null);

    try {
      const response = await apiFetch.get<QuerySiiStatusResponse>(
        `/api/tax-documents/${documentId}/status`
      );
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to query status");
      setError(error);
      throw error;
    } finally {
      setIsQuerying(false);
    }
  };

  return {
    submitDocuments,
    queryStatus,
    isSubmitting,
    isQuerying,
    error,
  };
}
